import React, { useRef, useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  Activity, MapPin, User, LayoutGrid as Menu, X,
  LogIn, LogOut, ShieldCheck, Stethoscope, Building2,
  Sun, Cloud, CloudRain, CloudSnow, CloudLightning, Wind,
  MessageCircle as MessageSquare, ShoppingBag, GraduationCap, Newspaper, ChevronDown, BarChart3,
} from 'lucide-react';
import { AccountType, ProviderRole } from '../types';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { motion, AnimatePresence } from 'motion/react';
import NotificationsBell from './NotificationsBell';
import { api } from '../services/homphyApi';

/* 
 * getUserDashboardPath
 * Single source of truth for "where does this logged-in user's
 * dashboard live". Mirrors AuthPage.roleDestination() exactly.
 *
 * BUG THIS FIXES: the header previously used ad-hoc ternaries that
 * only special-cased PHYSIOTHERAPY/ADMIN and fell back to `/dashboard`
 * (the CLIENT-only dashboard) for everyone else — CLINIC, EDUCATOR,
 * LEARNER, and the other 7 provider cadres. Since `/dashboard` is a
 * ProtectedRoute gated to AccountType.CLIENT, any non-client landing
 * there got bounced, which is what looked like dashboards "getting
 * mixed up" or "disappearing" — most visibly via the Messages icon,
 * which had the same bug plus no real ?tab=messages target for most
 * roles.
 *  */
function getUserDashboardPath(user: any): string {
  if (!user) return '/';
  if (user.accountType === AccountType.ADMIN) return '/admin';
  if (user.isLearner) return '/learn/dashboard';
  if (user.roles?.includes(ProviderRole.PHYSIOTHERAPY)) return '/physio-dashboard';
  if (user.roles?.includes(ProviderRole.EDUCATOR)) return '/teach';
  if (
    user.roles?.includes(ProviderRole.CLINIC) ||
    user.roles?.includes(ProviderRole.REFERRAL_GP) ||
    user.roles?.includes(ProviderRole.REFERRAL_CHW) ||
    user.roles?.includes(ProviderRole.OCCUPATIONAL_THERAPY) ||
    user.roles?.includes(ProviderRole.SPEECH_LANGUAGE_THERAPY) ||
    user.roles?.includes(ProviderRole.ORTHOTICS_PROSTHETICS) ||
    user.roles?.includes(ProviderRole.REHAB_PSYCHOLOGY) ||
    user.roles?.includes(ProviderRole.PHYSIATRY) ||
    user.roles?.includes(ProviderRole.REHAB_NURSING)
  ) {
    return '/provider-dashboard';
  }
  return '/dashboard';
}

// Only these dashboards currently accept a ?tab=messages deep-link.
// Everyone else goes to their normal dashboard root instead of being
// routed into the wrong (CLIENT-only) /dashboard?tab=messages, which
// is what broke providers/educators/clinics when they clicked the
// header Messages icon.
function getUserMessagesPath(user: any): string {
  const base = getUserDashboardPath(user);
  if (base === '/dashboard' || base === '/physio-dashboard' || base === '/provider-dashboard') {
    return `${base}?tab=messages`;
  }
  return base;
}

/* ── Weather theme map  */
type WeatherTheme = {
  label: string;
  icon: any;
  tileGrad: string;
  tintColor: string;
  glowA: string;
  glowB: string;
  ring1: string;
  ring2: string;
};

const WEATHER_THEMES: Record<string, WeatherTheme> = {
  sunny:   { label:'Clear',  icon: Sun,            tileGrad:'linear-gradient(145deg,#3ec28f 0%,#137a56 40%,#063d2b 100%)', tintColor:'rgba(255,220,80,0.18)',  glowA:'rgba(29,158,117,0.45)',  glowB:'rgba(245,166,35,0.3)',   ring1:'#1D9E75', ring2:'#0fbe88' },
  cloudy:  { label:'Cloudy', icon: Cloud,          tileGrad:'linear-gradient(145deg,#4ecba0 0%,#137a56 48%,#0a4632 100%)', tintColor:'rgba(160,200,180,0.14)', glowA:'rgba(29,158,117,0.35)',  glowB:'rgba(80,160,130,0.2)',  ring1:'#1D9E75', ring2:'#56c9a0' },
  rain:    { label:'Rainy',  icon: CloudRain,      tileGrad:'linear-gradient(145deg,#2fa87e 0%,#0e6e4d 45%,#053a29 100%)', tintColor:'rgba(60,180,140,0.18)',  glowA:'rgba(29,158,117,0.38)',  glowB:'rgba(60,140,110,0.3)',  ring1:'#1D9E75', ring2:'#30b890' },
  storm:   { label:'Storm',  icon: CloudLightning, tileGrad:'linear-gradient(145deg,#238f6b 0%,#0c6047 45%,#052e22 100%)', tintColor:'rgba(100,200,160,0.18)', glowA:'rgba(29,158,117,0.42)',  glowB:'rgba(60,180,130,0.35)', ring1:'#1D9E75', ring2:'#0fbe88' },
  snow:    { label:'Snow',   icon: CloudSnow,      tileGrad:'linear-gradient(145deg,#5fd9ac 0%,#137a56 45%,#0a4a35 100%)', tintColor:'rgba(200,255,240,0.2)',  glowA:'rgba(29,158,117,0.3)',   glowB:'rgba(180,255,230,0.3)', ring1:'#1D9E75', ring2:'#90ffdc' },
  wind:    { label:'Windy',  icon: Wind,           tileGrad:'linear-gradient(145deg,#38b98a 0%,#0e6e4d 48%,#063d2c 100%)', tintColor:'rgba(100,240,180,0.14)', glowA:'rgba(29,158,117,0.35)',  glowB:'rgba(29,158,117,0.3)',  ring1:'#1D9E75', ring2:'#2dd9a4' },
  default: { label:'',       icon: null,           tileGrad:'linear-gradient(145deg,#3ec28f 0%,#0f6e4d 42%,#053a29 100%)', tintColor:'rgba(0,0,0,0)',          glowA:'rgba(29,158,117,0.45)',  glowB:'rgba(13,110,80,0.3)',   ring1:'#1D9E75', ring2:'#0fbe88' },
};

function getThemeFromCode(code: number, wind: number): WeatherTheme {
  if (code === 0)                   return WEATHER_THEMES.sunny;
  if ([1,2,3,45,48].includes(code)) return WEATHER_THEMES.cloudy;
  if (code >= 51 && code <= 82)     return WEATHER_THEMES.rain;
  if (code >= 71 && code <= 77)     return WEATHER_THEMES.snow;
  if (code >= 85 && code <= 86)     return WEATHER_THEMES.snow;
  if (code >= 95)                   return WEATHER_THEMES.storm;
  if (wind > 40)                    return WEATHER_THEMES.wind;
  return WEATHER_THEMES.default;
}

function useLocalWeather() {
  const [weather, setWeather] = useState<{ theme: WeatherTheme; temp: number } | null>(null);
  useEffect(() => {
    if (!navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition(async ({ coords }) => {
      try {
        const res  = await fetch(`https://api.open-meteo.com/v1/forecast?latitude=${coords.latitude}&longitude=${coords.longitude}&current_weather=true`);
        const data = await res.json();
        const cw   = data.current_weather;
        setWeather({ theme: getThemeFromCode(cw.weathercode, cw.windspeed), temp: Math.round(cw.temperature) });
      } catch { /* silent */ }
    }, () => { /* denied */ });
  }, []);
  return weather;
}

/* ── Traffic heuristic 
   No live traffic feed at header scope (that lives in
   MapboxContainer's Directions/Traffic-v1 calls). This is a
   time-of-day estimate only — clearly labelled as such below —
   good enough for a passive nav hint, not for routing decisions. */
type TrafficStatus = { label: string; color: string };

function getTrafficStatus(): TrafficStatus {
  const hour = new Date().getHours();
  const isRush = (hour >= 7 && hour <= 9) || (hour >= 17 && hour <= 19);
  const isModerate = (hour >= 6 && hour < 7) || (hour > 9 && hour <= 11) || (hour >= 16 && hour < 17) || (hour > 19 && hour <= 21);
  if (isRush)     return { label: 'Heavy',   color: '#E4483B' };
  if (isModerate) return { label: 'Moderate', color: '#E4A93B' };
  return { label: 'Light', color: '#1D9E75' };
}

function useTrafficStatus(): TrafficStatus {
  const [status, setStatus] = useState<TrafficStatus>(() => getTrafficStatus());
  useEffect(() => {
    const id = setInterval(() => setStatus(getTrafficStatus()), 5 * 60 * 1000);
    return () => clearInterval(id);
  }, []);
  return status;
}

function getSuggestion(weather: { theme: WeatherTheme; temp: number } | null, traffic: TrafficStatus): string {
  if (traffic.label === 'Heavy')   return 'Build in extra travel time for home visits.';
  if (weather?.theme.label === 'Rainy' || weather?.theme.label === 'Storm')
    return 'Wet roads — confirm home-visit access with patients.';
  if (traffic.label === 'Light' && weather && weather.theme.label !== 'Rainy')
    return 'Clear roads — a good window for home visits.';
  return 'Good conditions for scheduled sessions.';
}

/* ── Custom traffic-light glyph (lucide has no direct equivalent) ── */
const TrafficLightIcon: React.FC<{ activeColor: string; size?: number }> = ({ activeColor, size = 15 }) => (
  <svg width={size} height={size * 1.6} viewBox="0 0 10 16" fill="none">
    <rect x="0.5" y="0.5" width="9" height="15" rx="2.5" fill="#1a2e20" />
    <circle cx="5" cy="4"    r="1.7" fill={activeColor === '#E4483B' ? '#E4483B' : 'rgba(255,255,255,0.15)'} />
    <circle cx="5" cy="8"    r="1.7" fill={activeColor === '#E4A93B' ? '#E4A93B' : 'rgba(255,255,255,0.15)'} />
    <circle cx="5" cy="12"   r="1.7" fill={activeColor === '#1D9E75' ? '#1D9E75' : 'rgba(255,255,255,0.15)'} />
  </svg>
);

/* Nav counts for Homphy Teams, kept on the server so every device agrees.
   Jobs link: unread "new Teams job" notices. Teams link: unread "new applicant" notices.
   Opening /open-jobs or /teams marks that page's notices as read. */
function useTeamsBadges(user: any) {
  const location = useLocation();
  const uid = user?.id;
  const [badges, setBadges] = useState({ newJobs: 0, newApplicants: 0 });

  useEffect(() => {
    if (!uid) { setBadges({ newJobs: 0, newApplicants: 0 }); return; }
    let cancelled = false;
    const path = location.pathname;
    const page = path.startsWith('/open-jobs') ? 'jobs' : path.startsWith('/teams') ? 'teams' : null;
    const run = async () => {
      if (page) { try { await api.post(`/notifications/teams-seen?page=${page}`); } catch { /* ignore */ } }
      try {
        const c = await api.get<{ jobs: number; applicants: number }>('/notifications/teams-counts');
        if (!cancelled) setBadges({ newJobs: c.jobs, newApplicants: c.applicants });
      } catch { /* offline */ }
    };
    void run();
    const id = window.setInterval(() => void run(), 60_000);
    return () => { cancelled = true; window.clearInterval(id); };
  }, [uid, location.pathname]);

  return badges;
}
export const Header: React.FC = () => {
  const [isOpen, setIsOpen] = React.useState(false);
  const location   = useLocation();
  const { user, logout } = useAuth();
  const { t, language, setLanguage } = useLanguage();
  const weather    = useLocalWeather();
  const traffic    = useTrafficStatus();
  const theme = weather?.theme ?? WEATHER_THEMES.default;
  const { newJobs, newApplicants } = useTeamsBadges(user);

  const logoRef    = useRef<HTMLDivElement>(null);
  const tintRef    = useRef<HTMLDivElement>(null);
  const badgeRef   = useRef<HTMLDivElement>(null);
  const pinRef     = useRef<any>(null);
  const ring1Ref   = useRef<SVGCircleElement>(null);
  const ring2Ref   = useRef<SVGCircleElement>(null);
  const lettersRef = useRef<HTMLSpanElement[]>([]);

  /* entrance + pin bob + rings */
  useEffect(() => {
    if (!logoRef.current || !pinRef.current) return;

    let cancelled = false;
    let cleanup = () => {};
    import('gsap').then(({ gsap }) => {
      if (cancelled) return;
      gsap.fromTo(logoRef.current,
        { scale: 0.4, opacity: 0, rotate: -8 },
        { scale: 1,   opacity: 1, rotate: 0, duration: 0.7, ease: 'back.out(2.2)', delay: 0.1 }
      );

      gsap.fromTo(lettersRef.current,
        { y: 10, opacity: 0, scale: 0.7 },
        { y: 0,  opacity: 1, scale: 1, duration: 0.4, ease: 'back.out(2)', stagger: 0.06, delay: 0.35 }
      );

      gsap.fromTo(pinRef.current,
        { y: -10, opacity: 0, scale: 1.3 },
        { y: 0,   opacity: 1, scale: 1,   duration: 0.55, ease: 'bounce.out', delay: 0.5 }
      );

      gsap.to(pinRef.current, {
        y: -2.5, duration: 1.2, ease: 'sine.inOut', yoyo: true, repeat: -1, delay: 1.1,
      });

      const ringAnim = (el: SVGCircleElement | null, delay: number) => {
        if (!el) return;
        gsap.fromTo(el,
          { attr:{ r:14 }, opacity:0.6 },
          { attr:{ r:26 }, opacity:0, duration:1.0, ease:'power1.out', repeat:-1, repeatDelay:0.6, delay }
        );
      };
      ringAnim(ring1Ref.current, 0);
      ringAnim(ring2Ref.current, 0.22);

      const tile    = logoRef.current;
      const onEnter = () => gsap.to(pinRef.current, { scale:1.3, y:-5, rotate:-8, duration:0.22, ease:'back.out(3)', transformOrigin:'50% 100%' });
      const onLeave = () => gsap.to(pinRef.current, { scale:1,   y:0,  rotate:0,  duration:0.4,  ease:'elastic.out(1,0.45)', transformOrigin:'50% 100%' });
      tile.addEventListener('mouseenter', onEnter);
      tile.addEventListener('mouseleave', onLeave);
      cleanup = () => { tile.removeEventListener('mouseenter', onEnter); tile.removeEventListener('mouseleave', onLeave); };
    });
    return () => { cancelled = true; cleanup(); };
  }, []);

  useEffect(() => {
    if (!weather || !tintRef.current || !badgeRef.current) return;
    import('gsap').then(({ gsap }) => {
      gsap.fromTo(tintRef.current,  { opacity:0 }, { opacity:1, duration:1.4, ease:'power2.out' });
      gsap.fromTo(badgeRef.current, { scale:0, opacity:0, rotate:-20 }, { scale:1, opacity:1, rotate:0, duration:0.5, ease:'back.out(2.5)', delay:0.4 });
    });
  }, [weather]);

  const isActive    = (path: string) => location.pathname === path;
  const LETTERS     = 'HOMPHY'.split('');
  const WeatherIcon = theme.icon;

  const NavLink: React.FC<{ to: string; icon: any; badge?: number; children: React.ReactNode }> = ({ to, icon: Icon, badge, children }) => (
    <Link
      to={to}
      onClick={() => setIsOpen(false)}
      className={`flex items-center gap-1.5 px-3 py-2.5 rounded-xl text-sm font-bold whitespace-nowrap transition-all ${
        isActive(to)
          ? 'bg-brand-50 text-brand-700'
          : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'
      }`}
    >
      <span className="relative inline-flex">
        <Icon className="w-4 h-4" />
        {badge ? <span className="absolute -right-2 -top-2 inline-flex h-4 min-w-4 items-center justify-center rounded-full bg-red-500 px-1 text-[9px] font-black text-white">{badge > 9 ? '9+' : badge}</span> : null}
      </span>
      {children}
    </Link>
  );

  const ADMIN_MENU_ITEMS = [
    { to: "/admin/workforce", label: "Workforce" },
    { to: "/admin/access-impact", label: "Access & Impact" },
    { to: "/impact", label: "Public Impact" },
  ];

  const AdminMenu: React.FC = () => {
    const [open, setOpen] = React.useState(false);
    const ref = React.useRef<HTMLDivElement>(null);
    React.useEffect(() => {
      function onDocClick(e: MouseEvent) {
        if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
      }
      document.addEventListener("mousedown", onDocClick);
      return () => document.removeEventListener("mousedown", onDocClick);
    }, []);
    const isAdminActive = ADMIN_MENU_ITEMS.some(i => isActive(i.to));
    return (
      <div ref={ref} style={{ position: "relative" }}>
        <button
          onClick={() => setOpen(o => !o)}
          className={`flex items-center gap-1.5 px-3 py-2.5 rounded-xl text-sm font-bold whitespace-nowrap transition-all ${
            isAdminActive ? "bg-brand-50 text-brand-700" : "text-slate-600 hover:bg-slate-50 hover:text-slate-900"
          }`}
        >
          <ShieldCheck className="w-4 h-4" />
          Admin
          <ChevronDown className="w-3.5 h-3.5" />
        </button>
        {open && (
          <div style={{ position: "absolute", top: "110%", right: 0, background: "#fff", border: "1px solid rgba(20,40,30,0.08)", borderRadius: 12, boxShadow: "0 8px 24px rgba(20,40,30,0.12)", minWidth: 180, overflow: "hidden", zIndex: 60 }}>
            {ADMIN_MENU_ITEMS.map(item => (
              <Link
                key={item.to}
                to={item.to}
                onClick={() => setOpen(false)}
                style={{ display: "block", padding: "10px 14px", fontSize: "0.82rem", fontWeight: 700, color: isActive(item.to) ? "#1D9E75" : "#334155", textDecoration: "none" }}
              >
                {item.label}
              </Link>
            ))}
          </div>
        )}
      </div>
    );
  };

  return (
    <nav className="sticky top-0 z-50 bg-white/95 backdrop-blur-md border-b border-slate-100 shadow-sm">
      <div className="max-w-7xl mx-auto px-4">
        <div className="flex items-center justify-between h-16">

          {/* ── Logo + weather/traffic badge ── */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <Link to="/" className="flex items-center gap-3">
            <div
              ref={logoRef}
              style={{
                width: 38, height: 38, borderRadius: 12, flexShrink: 0,
                background: theme.tileGrad,
                boxShadow: `0 0 0 1px rgba(255,255,255,0.12) inset, 0 4px 12px ${theme.glowA}, 0 8px 24px ${theme.glowB}`,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                position: 'relative', overflow: 'hidden', cursor: 'pointer',
              }}
            >
              {/* Tint overlay for weather crossfade */}
              <div
                ref={tintRef}
                style={{
                  position: 'absolute', inset: 0, opacity: 0,
                  background: theme.tintColor,
                  borderRadius: 'inherit', pointerEvents: 'none',
                }}
              />
              {/* Pulse rings */}
              <svg style={{ position:'absolute', inset:0, width:'100%', height:'100%', overflow:'visible' }} viewBox="0 0 38 38">
                <circle ref={ring1Ref} cx="19" cy="19" r="14" fill="none" stroke={theme.ring1} strokeWidth="1.5" opacity="0" />
                <circle ref={ring2Ref} cx="19" cy="19" r="14" fill="none" stroke={theme.ring2} strokeWidth="1"   opacity="0" />
              </svg>
              {/* Pin icon */}
              <MapPin
                ref={pinRef}
                fill="white"
                stroke="none"
                style={{ width:17, height:17, position:'relative', zIndex:1, filter:`drop-shadow(0 1px 3px ${theme.glowA})` }}
              />
            </div>

            <div style={{ display:'flex', alignItems:'baseline', gap:0 }}>
              {'HOMPHY'.split('').map((letter, i) => (
                <span
                  key={i}
                  ref={el => { if (el) lettersRef.current[i] = el; }}
                  style={{
                    fontSize:'1.05rem', fontWeight:900, letterSpacing:'0.09em',
                    color: i < 3 ? '#1a2e20' : '#1D9E75',
                    lineHeight:1,
                  }}
                >
                  {letter}
                </span>
              ))}
            </div>

          </Link>

          {weather && (
            <div
              ref={badgeRef}
              style={{
                display: 'flex', alignItems: 'center', gap: 6,
                padding: '4px 9px', borderRadius: 10,
                background: 'rgba(29,158,117,0.06)',
                border: '1px solid rgba(29,158,117,0.12)',
                flexShrink: 0,
              }}
            >
              <WeatherIcon size={14} color={theme.ring1} strokeWidth={2} />
              <span style={{ fontSize: '0.78rem', fontWeight: 800, color: '#0f1f15', letterSpacing: '-0.02em' }}>
                {Math.round(weather.temp)}°
              </span>
              <span style={{ width: 1, height: 12, background: 'rgba(29,158,117,0.18)', display: 'inline-block' }} />
              <TrafficLightIcon activeColor={traffic.color} size={11} />
            </div>
          )}
          </div>

          {/* ── Desktop nav ── */}
          <div className="hidden md:flex items-center space-x-1">
            {/*
              "Find Physiotherapist" links to home with ?match=true.
              Home.tsx detects this param → triggers AI matching overlay
              + smooth-scrolls to the #find map section.
            */}
            {/* ── Find Provider / Shop / Learn ── */}
            <NavLink to="/?section=map" icon={MapPin}>{t('Find Provider', 'Tafuta Mtaalamu')}</NavLink>
            <NavLink to="/shop" icon={ShoppingBag}>{t('Shop', 'Duka')}</NavLink>
            <NavLink to="/learn" icon={GraduationCap}>{t('Learn', 'Jifunze')}</NavLink>
            <NavLink to="/articles" icon={Newspaper}>{t('Articles', 'Makala')}</NavLink>
            <NavLink to="/impact" icon={BarChart3}>{t('Impact', 'Athari')}</NavLink>
                <NavLink to="/teams" icon={User} badge={newApplicants}>{t('Teams', 'Timu')}</NavLink>
                {user?.roles?.includes(ProviderRole.PHYSIOTHERAPY) && <NavLink to="/open-jobs" icon={Activity} badge={newJobs}>{t('Jobs', 'Kazi')}</NavLink>}
            <button
              onClick={() => setLanguage(language === 'en' ? 'sw' : 'en')}
              style={{ display: 'flex', alignItems: 'center', gap: 4, background: '#0f2419', border: '1px solid rgba(255,255,255,0.18)', borderRadius: 100, padding: '3px 4px', cursor: 'pointer' }}
            >
              <span style={{ fontSize: '0.62rem', fontWeight: 700, letterSpacing: '0.04em', padding: '3px 8px', borderRadius: 100, color: language === 'en' ? '#fff' : '#9fc2ab', background: language === 'en' ? '#0f8a63' : 'transparent', transition: 'all 0.2s ease' }}>
                EN
              </span>
              <span style={{ fontSize: '0.62rem', fontWeight: 700, letterSpacing: '0.04em', padding: '3px 8px', borderRadius: 100, color: language === 'sw' ? '#fff' : '#9fc2ab', background: language === 'sw' ? '#0f8a63' : 'transparent', transition: 'all 0.2s ease' }}>
                SW
              </span>
            </button>
            <div className="h-6 w-px bg-slate-100 mx-2" />
            {user ? (
              <div className="flex items-center gap-2">
                <NotificationsBell />
                <NavLink to="/payments" icon={BarChart3}>{t('Payments', 'Malipo')}</NavLink>
                
                <NavLink
                  to={getUserDashboardPath(user)}
                  icon={User}
                >
                  {user?.name?.split(' ')[0] || 'User'}
                </NavLink>
                {user?.accountType === AccountType.ADMIN && <AdminMenu />}
                <button onClick={logout} className="p-2 text-slate-400 hover:text-red-500 transition-colors">
                  <LogOut className="w-5 h-5" />
                </button>
              </div>
            ) : (
              <div className="flex items-center gap-3">
                <Link to="/login" className="btn-primary py-2 px-5 text-sm flex items-center gap-2">
                  <LogIn className="w-4 h-4" /> {t('Sign In', 'Ingia')}
                </Link>
              </div>
            )}
          </div>

          {/* ── Mobile hamburger ── */}
          <div className="md:hidden flex items-center">
            <button onClick={() => setIsOpen(!isOpen)} className="p-2 text-slate-600">
              {isOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>
      </div>

      {/* ── Mobile menu ── */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity:0, height:0 }}
            animate={{ opacity:1, height:'auto' }}
            exit={{   opacity:0, height:0 }}
            className="md:hidden bg-white border-t border-slate-50 overflow-hidden shadow-xl"
          >
            <div className="px-4 pt-2 pb-6 space-y-2">
              <NavLink to="/" icon={Activity}>Home</NavLink>
              <NavLink to="/?section=map" icon={MapPin}>{t('Find Provider', 'Tafuta Mtaalamu')}</NavLink>
              <NavLink to="/shop" icon={ShoppingBag}>{t('Shop', 'Duka')}</NavLink>
              <NavLink to="/learn" icon={GraduationCap}>{t('Learn', 'Jifunze')}</NavLink>
              <NavLink to="/articles" icon={Newspaper}>{t('Articles', 'Makala')}</NavLink>
              <NavLink to="/impact" icon={BarChart3}>{t('Impact', 'Athari')}</NavLink>
                <NavLink to="/teams" icon={User} badge={newApplicants}>{t('Teams', 'Timu')}</NavLink>
                {user?.roles?.includes(ProviderRole.PHYSIOTHERAPY) && <NavLink to="/open-jobs" icon={Activity} badge={newJobs}>{t('Jobs', 'Kazi')}</NavLink>}
              <button
                onClick={() => setLanguage(language === 'en' ? 'sw' : 'en')}
                style={{ display: 'flex', alignItems: 'center', gap: 4, background: '#0f2419', border: '1px solid rgba(255,255,255,0.18)', borderRadius: 100, padding: '4px 4px', cursor: 'pointer', margin: '0.5rem 0 0 1rem' }}
              >
                <span style={{ fontSize: '0.68rem', fontWeight: 700, letterSpacing: '0.04em', padding: '4px 10px', borderRadius: 100, color: language === 'en' ? '#fff' : '#9fc2ab', background: language === 'en' ? '#0f8a63' : 'transparent', transition: 'all 0.2s ease' }}>
                  EN
                </span>
                <span style={{ fontSize: '0.68rem', fontWeight: 700, letterSpacing: '0.04em', padding: '4px 10px', borderRadius: 100, color: language === 'sw' ? '#fff' : '#9fc2ab', background: language === 'sw' ? '#0f8a63' : 'transparent', transition: 'all 0.2s ease' }}>
                  SW
                </span>
              </button>
              {!user ? (
                <div className="flex flex-col gap-3 pt-4 border-t border-slate-50 mt-4">
                  <Link
                    to="/login"
                    onClick={() => setIsOpen(false)}
                    className="btn-primary flex justify-center gap-2"
                  >
                    <LogIn className="w-4 h-4" /> Sign In
                  </Link>
                </div>
              ) : (
                <>
                  <NavLink
                    to={getUserDashboardPath(user)}
                    icon={User}
                  >
                    Dashboard
                  </NavLink>
                  <NavLink to="/payments" icon={BarChart3}>{t('Payments', 'Malipo')}</NavLink>
                  {user?.accountType === AccountType.ADMIN && (
                    <>
                  <NavLink to="/admin/workforce" icon={User}>Workforce</NavLink>
                  <NavLink to="/admin/engagement-payouts" icon={User}>Job payouts</NavLink>
                </>
                  )}
                  {user?.accountType === AccountType.ADMIN && (
                    <NavLink to="/admin/access-impact" icon={User}>Access & Impact</NavLink>
                  )}
                  
                  <button
                    onClick={() => { logout(); setIsOpen(false); }}
                    className="w-full flex items-center gap-2 px-4 py-3 text-red-600 font-bold hover:bg-red-50 rounded-xl"
                  >
                    <LogOut className="w-4 h-4" /> Logout
                  </button>
                </>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
  );
};

export const Footer: React.FC = () => (
  <footer className="bg-white text-slate-500 border-t border-slate-100">
    <div className="max-w-7xl mx-auto px-4 py-16">
      <div className="grid grid-cols-1 md:grid-cols-5 gap-12">
        <div className="space-y-6 md:col-span-2">
          <div className="flex items-center gap-2">
            <div style={{ width:32, height:32, borderRadius:10, background:'linear-gradient(145deg,#4dffc0 0%,#1D9E75 55%,#0a5e40 100%)', display:'flex', alignItems:'center', justifyContent:'center', boxShadow:'0 2px 8px rgba(29,158,117,0.45)' }}>
              <MapPin size={15} strokeWidth={0} fill="white" />
            </div>
            <span className="font-black text-lg tracking-tight uppercase" style={{ color: '#0f1f15' }}>HOMPHY</span>
          </div>
          <p className="text-sm leading-relaxed max-w-sm">
            Leading Kenya's rehabilitation access revolution. We connect precision care with patient
            needs across every rehab cadre — physio, occupational therapy, speech therapy, and more — using verified data and seamless technology.
          </p>
          <div className="flex items-center gap-2 text-xs font-black uppercase tracking-widest" style={{ color: '#1D9E75' }}>
            <ShieldCheck className="w-4 h-4" /> Regulatory Compliance Engine
          </div>
        </div>

        <div>
          <h4 className="font-bold mb-6 text-sm uppercase tracking-widest" style={{ color: '#0f1f15' }}>Platform</h4>
          <ul className="space-y-3 text-sm font-medium">
            {/* Footer find links → home with ?match=true (same as nav) */}
            <li><Link to="/?match=true&type=physio"  className="hover:text-brand-500 transition-colors">Find a Rehab Professional</Link></li>
            <li><Link to="/register"              className="hover:text-brand-500 transition-colors">Join as a Rehab Professional</Link></li>
            <li><Link to="/register?portal=patient" className="hover:text-brand-500 transition-colors">Join as Patient</Link></li>
          </ul>
        </div>

        <div>
          <h4 className="font-bold mb-6 text-sm uppercase tracking-widest" style={{ color: '#0f1f15' }}>Learn & Shop</h4>
          <ul className="space-y-3 text-sm font-medium">
            <li><Link to="/shop"           className="hover:text-brand-500 transition-colors">Shop Rehabilitative Aids</Link></li>
            <li><Link to="/learn"          className="hover:text-brand-500 transition-colors">Take a rehabilitative course</Link></li>
            <li><Link to="/learn"          className="hover:text-brand-500 transition-colors">Teach & Earn</Link></li>
            <li><Link to="/articles"       className="hover:text-brand-500 transition-colors">Articles</Link></li>
            <li><Link to="/impact"         className="hover:text-brand-500 transition-colors">Access & Impact</Link></li>
          </ul>
        </div>

        <div>
          <h4 className="font-bold mb-6 text-sm uppercase tracking-widest" style={{ color: '#0f1f15' }}>Support</h4>
          <ul className="space-y-3 text-sm font-medium">
            <li><Link to="/help"                 className="hover:text-brand-500 transition-colors">Help Center</Link></li>
            <li><Link to="/professional-rules"   className="hover:text-brand-500 transition-colors">Professional Rules</Link></li>
            <li><Link to="/cross-border-policy"  className="transition-colors" style={{ color: '#1D9E75' }}>Cross-Border Rules</Link></li>
            <li><Link to="/patients-rights"      className="hover:text-brand-500 transition-colors">Patient Rights</Link></li>
          </ul>
          <div className="mt-6 pt-6 border-t border-slate-100 text-xs">
            <p className="italic" style={{ color: '#0f1f15' }}>Nakuru, Kenya</p>
            <p className="font-black uppercase tracking-widest not-italic mt-1 text-slate-400">
              Full support: +254 792 606 719
            </p>
          </div>
        </div>
      </div>

      <div className="mt-16 pt-8 border-t border-slate-100 text-center text-xs font-bold uppercase tracking-widest flex flex-col md:flex-row justify-between items-center gap-4">
        <span className="text-slate-400">&copy; {new Date().getFullYear()} Homphy Physical Rehabilitation Services. All rights reserved.</span>
<div className="flex gap-6 text-slate-400">
          <Link to="/how-we-verify"    className="hover:text-brand-500 cursor-pointer transition-colors">How We Verify Providers</Link>
          <Link to="/privacy-policy"   className="hover:text-brand-500 cursor-pointer transition-colors">Privacy Policy</Link>
          <Link to="/terms-of-service" className="hover:text-brand-500 cursor-pointer transition-colors">Terms of Service</Link>
        </div>
      </div>
    </div>
  </footer>
);

export const Layout: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="min-h-screen flex flex-col selection:bg-brand-500 selection:text-white overflow-x-hidden">
    <Header />
    <main className="flex-grow">{children}</main>
    <Footer />
  </div>
);


